import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuSearch } from 'react-icons/lu';
import { PageLayout } from '../../../app/PageLayout';
import { Button, EmptyState } from '../../../components/ui';
import { plural, t } from '../../../i18n';
import { ptBR } from '../../../i18n/pt-BR';
import { Patient } from '../types';
import { RowButton, RowSkeletons } from '../RowButton';
import { SearchField } from '../../../components/patterns/SearchField';
import { PATHS } from "../../../app/paths";

function getInitials(name: string): string {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface PatientListScreenProps {
  patients: Patient[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  onPatientSelect: (patient: Patient) => void;
}

export const PatientListScreen: React.FC<PatientListScreenProps> = ({ patients, loading, failed, onRetry, onPatientSelect }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = patients.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <PageLayout title={t('savedOrders.title')}>
      <SearchField sticky
        value={search}
        onChange={setSearch}
        placeholder={t('savedOrders.searchPatient')}
        disabled={loading || failed || patients.length === 0}
      />

      {loading ? (
        <div className="flex flex-col gap-2.5"><RowSkeletons /></div>
      ) : failed ? (
        <EmptyState
          title={t('savedOrders.loadError')}
          description={t('savedOrders.loadErrorHint')}
          action={<Button variant="outline" size="sm" onClick={onRetry}>{t('common.retry')}</Button>}
        />
      ) : patients.length === 0 ? (
        <EmptyState
          title={t('savedOrders.empty')}
          description={t('savedOrders.emptyHint')}
          action={<Button variant="primary" size="sm" onClick={() => navigate(PATHS.newOrder)}>{t('savedOrders.createOrder')}</Button>}
        />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 py-8 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-primary-tint text-primary"><LuSearch size={28} aria-hidden /></span>
          <p className="text-fg-muted">{t('savedOrders.noPatient')}<br />{t('savedOrders.noPatientHint')}</p>
        </div>
      ) : (
        <div className="stagger flex flex-col gap-2.5">
          {filtered.map((patient) => (
            <RowButton
              key={patient.id}
              avatar={getInitials(patient.name)}
              title={patient.name}
              subtitle={plural(patient.ordersCount, ptBR.savedOrders.orderCount)}
              onClick={() => onPatientSelect(patient)}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
};
